// 네이티브 껍데기(Tauri/Electron) 연동 표면.
//
// 통합앱에서는 얇은 네이티브 껍데기가 이 웹 뷰어(webview)를 로드하고, 로드 전에 `window.__RS_SHELL__` 로
// 설정을 주입한다: 서버 주소, 그리고 **이 기기(피제어)의 접속 ID·세션 비밀번호**(껍데기의 네이티브 에이전트가 제공).
// 순수 웹(브라우저 직접 접속)에서는 `__RS_SHELL__` 이 없으므로 SHELL 은 빈 객체, IS_SHELL=false 다.
//
// 이렇게 두면 웹 뷰어(제어 UI)는 단일 소스로 유지하고, 플랫폼별 네이티브 부분(피제어 정체성 등)은
// 껍데기가 주입하는 데이터로만 반영한다(설계 §3 층 구조).

export interface ShellConfig {
  /** 서버 URL(껍데기가 주입/저장). config.ts 의 서버 결정 우선순위에 포함. */
  server?: string;
  /** 이 기기(피제어)의 접속 ID(deviceCode) — 껍데기의 네이티브 에이전트가 제공. */
  passiveId?: string;
  /** 이 기기의 세션 비밀번호 — 껍데기가 생성/재생성해 제공(TeamViewer 식). */
  sessionPassword?: string;
  /** 이 기기에 무인(Easy Access) 비밀번호가 설정돼 있는지 — 껍데기가 에이전트 상태로 주입. */
  unattendedSet?: boolean;
  /** "다음부터 묻지 않고 접속" 으로 비밀번호 없이 허용한 계정 수(에이전트 `trustedCount`). */
  trustedCount?: number;
  /**
   * 이 기기가 원격 입력 제어를 지금 허용하고 있는지 (A4 실시간 스위치).
   *
   * ⚠️ 미주입(`undefined`)은 **허용**으로 읽어야 한다 — 기본값이 허용이고, 값을 모른다고 해서
   * 화면이 "차단됨" 으로 보이면 사용자가 있지도 않은 문제를 고치려 든다.
   */
  controlAllowed?: boolean;
  /** 이 기기가 `--view-only` 로 고정돼 사용자가 그 스위치를 켤 수 없는지(관리자 설정). */
  controlLocked?: boolean;
  /** 껍데기 종류(진단/분기용, 예: 'tauri' | 'electron'). */
  platform?: string;
  /**
   * 앱 버전(예: '0.4.7'). 창에 표시한다 — 아이콘이 전 버전 동일하고 버전 표시가 없어, 사용자가 정상 업데이트를
   * "안 된 것 같다" 고 판단한 일이 있었다(2026-09-10 Mac 실기).
   */
  appVersion?: string;
  /**
   * 이 PC 가 **무인 원격지원 대상**이고, 이 창이 그 서비스에 붙어 있는지.
   *
   * true 면 화면의 주인공이 달라진다 — 이 앞의 사람은 **남을 제어하러 온 것이 아니라
   * 지원을 받으려는 사람**이다. 내 연결 패널을 앞세우고 뷰어 로그인은 접어 둔다.
   * 신원(기기 코드·비밀번호)은 이 창이 아니라 **서비스가 소유**한다.
   */
  unattendedHost?: boolean;
  /**
   * "로그인 시 자동 실행(무인 대기)" 을 이 OS 에서 지원하는지 — 현재 macOS 만(Windows 는 무인 서비스가 따로 있다).
   * false/미주입이면 스위치를 그리지 않는다.
   */
  autostartSupported?: boolean;
  /** 로그인 시 자동 실행이 지금 켜져 있는지(앱 기동 시점 값 — 이후 변경은 `set_autostart` 반환값으로 반영). */
  autostartEnabled?: boolean;
}

declare global {
  interface Window {
    __RS_SHELL__?: ShellConfig;
  }
}

/** 껍데기가 주입한 설정(없으면 빈 객체). */
export const SHELL: ShellConfig =
  (typeof window !== 'undefined' && window.__RS_SHELL__) || {};

/** 네이티브 껍데기 안에서 실행 중인지 여부. */
export const IS_SHELL: boolean =
  typeof window !== 'undefined' && !!window.__RS_SHELL__;
